import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Box from '@mui/material/Box';

// Read-only overview map for the Case Timeline Builder: every geolocated
// event plotted as a category-colored dot, fit to bounds once on load. Same
// white-ring circleMarker convention as LPRMapReplay.js/CameraMapModal.js.

const DEFAULT_CENTER = { lat: 41.03, lng: -73.76, zoom: 12 };

export default function CaseOverviewMap({ points = [], onPointClick, height = 420 }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const didFitRef = useRef(false);
  const propsRef = useRef({});
  propsRef.current = { onPointClick };

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, { zoomControl: true, attributionControl: true });
    map.setView([DEFAULT_CENTER.lat, DEFAULT_CENTER.lng], DEFAULT_CENTER.zoom);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19, attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    const t = setTimeout(() => { if (mapRef.current === map) map.invalidateSize(false); }, 60);
    return () => {
      clearTimeout(t);
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
      didFitRef.current = false;
    };
  }, []);

  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.clearLayers();
    const pts = points.filter(p => p.lat != null && p.lng != null);
    for (const p of pts) {
      const m = L.circleMarker([p.lat, p.lng], {
        radius: 8, color: '#fff', weight: 2.5, fillColor: p.color || '#334155', fillOpacity: 1,
      });
      if (p.label) m.bindTooltip(p.label, { direction: 'top' });
      m.on('click', () => propsRef.current.onPointClick && propsRef.current.onPointClick(p.id));
      m.addTo(layer);
    }
    if (!didFitRef.current && pts.length && mapRef.current) {
      const b = L.latLngBounds(pts.map(p => [p.lat, p.lng]));
      if (b.isValid()) {
        mapRef.current.fitBounds(b.pad(0.25), { maxZoom: 16 });
        didFitRef.current = true;
      }
    }
  }, [points]);

  return <Box ref={containerRef} sx={{ height, width: '100%', borderRadius: '10px', overflow: 'hidden', zIndex: 0 }} />;
}
